import React from 'react';
import Icon from '../../../components/AppIcon';

const ROIComparison = () => {
  const fullTimeCDO = {
    title: 'Full-Time CDO',
    annualCost: 180000,
    benefits: 45000,
    recruitment: 30000,
    onboarding: 20000,
    timeToValue: '6-9 months',
    commitment: 'Long-term hire',
    flexibility: 'Low',
    costs: [
      { label: 'Annual Salary', value: 180000 },
      { label: 'Benefits & Overhead', value: 45000 },
      { label: 'Recruitment Costs', value: 30000 },
      { label: 'Onboarding & Training', value: 20000 }
    ]
  };

  const fractionalCDO = {
    title: 'Fractional CDO',
    annualCost: 240000,
    benefits: 0,
    recruitment: 0,
    onboarding: 0,
    timeToValue: '2-4 weeks',
    commitment: 'Flexible engagement',
    flexibility: 'High',
    costs: [
      { label: 'Monthly Engagement (€20k x 12)', value: 240000 },
      { label: 'Benefits & Overhead', value: 0 },
      { label: 'Recruitment Costs', value: 0 },
      { label: 'Onboarding & Training', value: 0 }
    ]
  };

  const fullTimeTotal = fullTimeCDO?.annualCost + fullTimeCDO?.benefits + fullTimeCDO?.recruitment + fullTimeCDO?.onboarding;
  const fractionalTotal = fractionalCDO?.annualCost;
  const savings = fullTimeTotal - fractionalTotal;
  const savingsPercentage = Math.round((savings / fullTimeTotal) * 100);

  return (
    <div>
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-bold mb-4">Investment Comparison</h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Fractional CDO services vs full-time executive hiring: cost analysis and time-to-value metrics
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Full-Time CDO */}
        <div className="bg-white rounded-xl p-6 shadow-elevation border border-border">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold">{fullTimeCDO?.title}</h3>
            <div className="w-12 h-12 bg-muted/20 rounded-lg flex items-center justify-center">
              <Icon name="User" size={24} className="text-muted-foreground" />
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {fullTimeCDO?.costs?.map((cost, index) => (
              <div key={index} className="flex justify-between items-center pb-3 border-b border-border last:border-0">
                <span className="text-muted-foreground">{cost?.label}</span>
                <span className="font-semibold">€{cost?.value?.toLocaleString()}</span>
              </div>
            ))}
          </div>

          <div className="bg-surface rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total First Year Cost</span>
              <span className="text-2xl font-bold">€{fullTimeTotal?.toLocaleString()}</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Time to Value</span>
              <span className="text-sm font-medium">{fullTimeCDO?.timeToValue}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Commitment</span>
              <span className="text-sm font-medium">{fullTimeCDO?.commitment}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Flexibility</span>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 rounded-full bg-muted"></div>
                <span className="text-sm font-medium">{fullTimeCDO?.flexibility}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Fractional CDO */}
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-6 shadow-elevation border-2 border-primary">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold">{fractionalCDO?.title}</h3>
              <div className="text-sm text-secondary font-medium">Recommended</div>
            </div>
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Crown" size={24} className="text-white" />
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {fractionalCDO?.costs?.map((cost, index) => (
              <div key={index} className="flex justify-between items-center pb-3 border-b border-border last:border-0">
                <span className="text-muted-foreground">{cost?.label}</span>
                <span className="font-semibold">€{cost?.value?.toLocaleString()}</span>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total First Year Cost</span>
              <span className="text-2xl font-bold text-primary">€{fractionalTotal?.toLocaleString()}</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Time to Value</span>
              <div className="flex items-center space-x-1">
                <Icon name="Zap" size={14} className="text-secondary" />
                <span className="text-sm font-medium text-secondary">{fractionalCDO?.timeToValue}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Commitment</span>
              <span className="text-sm font-medium">{fractionalCDO?.commitment}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Flexibility</span>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 rounded-full bg-secondary"></div>
                <span className="text-sm font-medium text-secondary">{fractionalCDO?.flexibility}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Savings Highlight */}
      <div className="bg-gradient-to-r from-secondary/10 to-primary/10 rounded-xl p-8 border border-secondary/20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="text-4xl font-bold text-secondary mb-2">€{Math.abs(savings)?.toLocaleString()}</div>
            <div className="text-muted-foreground">First Year Savings</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-primary mb-2">{Math.abs(savingsPercentage)}%</div>
            <div className="text-muted-foreground">Cost Reduction</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-primary mb-2">3-4x</div>
            <div className="text-muted-foreground">Faster Time to Value</div>
          </div>
        </div>
      </div>
      {/* Additional Benefits */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            icon: 'TrendingUp',
            title: 'Immediate Impact',
            description: 'Start delivering value in weeks, not months, with experienced leadership'
          },
          {
            icon: 'Shield',
            title: 'Reduced Risk',
            description: 'No long-term commitment or hiring risk—scale engagement as needed'
          },
          {
            icon: 'Users',
            title: 'Team Development',
            description: 'Build internal capability while receiving executive-level guidance'
          }
        ]?.map((benefit, index) => (
          <div key={index} className="bg-white rounded-lg p-6 border border-border">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <Icon name={benefit?.icon} size={20} className="text-primary" />
            </div>
            <h4 className="font-semibold mb-2">{benefit?.title}</h4>
            <p className="text-sm text-muted-foreground">{benefit?.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ROIComparison;